import * as StateLib from "@/Utils/StateLib";
import { Overlay, Tree, Select, Button, Divider, Flex } from "@theorgchartdev/storybook";
import Dragger from "../../Utils/Dragger/Dragger";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { useRouteState } from "@/hooks/useRouteState";
import RenamePopover from "@/appComponents/Blocks/Composites/RenamePopover/RenamePopover";
import React from "react";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { buildSCTree, handleSubChartClick } from "@/Utils/subchartNav";

const shuffleSubChart = (aSubChart, shuffleUp) => {
  let w = StateLib.GetWSMgr();
  w.StartCheckPoint();
  var p = aSubChart.GetSubChartParent();
  var swapIndex = 0;
  var curIndex = p.GetChildIndex(aSubChart);
  if (shuffleUp) swapIndex = curIndex - 1;
  else swapIndex = curIndex + 1;
  p.SwapChildren(curIndex, swapIndex);
  w.EndCheckPoint();

  w.UpdateReactContext();
};

const subchartShuffleOkay = (aSubChart, up) => {
  var upOK = false;
  var downOK = false;

  var p = aSubChart.GetSubChartParent();
  if (p != null) {
    var n = p.ChildCount();
    if (n > 1) {
      var i = p.GetChildIndex(aSubChart);
      upOK = i > 0;
      downOK = i < n - 1;
    }
  }
  if (up) return upOK;
  return downOK;
};

const SubchartsCard = ({ show, onClose }) => {
  const { setParam: setRouteParam } = useRouteState();
  const { handleSideBarModeChange } = useRightSidebarContext();

  if (!show) {
    return null;
  }

  const baseClass = "ocn-subchart-card";
  const doc = StateLib.GetDoc();
  const wsMgr = StateLib.GetWSMgr();
  const scm = doc?.GetSubChartMgr();
  const rootSubChart = scm?.GetRootSubChart();

  if (!rootSubChart) {
    return null;
  }

  const data = [
    {
      label: rootSubChart.GetSubChartName(),
      value: rootSubChart.GetSubChartID(),
      subTree: buildSCTree(rootSubChart)
    }
  ];

  const allTrees = scm.GetDisconnectedBranches();
  const currentTree = allTrees.find(subchart => subchart.scID === rootSubChart.GetSubChartID());
  const treeOptions = allTrees.map(subChart => ({
    label: `${subChart.scName} (Headcount: ${subChart.Headcount})`,
    value: `${subChart.treeID}-${subChart.TopBoxRef}`,
    clickHandler: chart => {
      // substring because top box ids can contain '-'
      const topBoxRef = chart.value.substring(chart.value.indexOf("-") + 1);
      setRouteParam("topOfChart", topBoxRef);
    }
  }));

  const curSC = scm.GetCurrentSubChart();

  const showEditControls = Globals._uiConfig.InEditMode() && !doc.IsPlanChart();

  const switchToOptimize = () => {
    handleSideBarModeChange("optimize");
  };

  const renameSubChart = newName => {
    wsMgr.StartCheckPoint();
    scm.UpdateName(curSC, newName);
    wsMgr.EndCheckPoint();
  };

  const headerActions = {
    closeAction: onClose
  };
  if (showEditControls)
    headerActions["customAction"] = {
      icon: "bolt",
      buttonProps: {},
      onClick: switchToOptimize
    };

  return (
    <Dragger>
      {ref => (
        <Overlay
          ref={ref}
          id={"subchart-card"}
          show={true}
          headerProps={{
            title: appstr("Subcharts"),
            headerActions: headerActions
          }}
          sizeProps={{
            minWidth: 320,
            maxWidth: 320,
            minHeight: 450,
            maxHeight: 450
          }}
        >
          {showEditControls && (
            <div style={{ paddingBottom: "4px" }}>
              <Flex direction="row" alignItems={"end"} padding={"0 0 8px 0"} gap="8px">
                <Button
                  size={"small"}
                  disabled={!subchartShuffleOkay(curSC, true)}
                  onClick={() => shuffleSubChart(curSC, true)}
                  modifier={"tool"}
                  iconOnly={true}
                  iconProps={{ name: "caret-up" }}
                  className={"oc-right-justify"}
                  dataTestId={"sc-shuffle-up-btn"}
                ></Button>
                <Button
                  size={"small"}
                  disabled={!subchartShuffleOkay(curSC, false)}
                  onClick={() => shuffleSubChart(curSC, false)}
                  modifier={"tool"}
                  iconOnly={true}
                  iconProps={{ name: "angle-down" }}
                  dataTestId={"sc-shuffle-down-btn"}
                ></Button>
                <RenamePopover
                  iconProps={{ disabled: false }}
                  name={curSC ? curSC.GetSubChartName() : ""}
                  onClose={() => {}}
                  //className={"oc-right-justify"}
                  onCommit={renameSubChart}
                  validateFxn={null}
                  triggerProps={{
                    tooltipProps: { message: appstr("Rename") }
                  }}
                  dataTestPrefix={"sc-card"}
                />
              </Flex>
              <Divider padding-bottom={"8px"} />
            </div>
          )}
          <div className={baseClass}>
            {allTrees.length > 1 && (
              <div className="ocn-overlay-select">
                <Select
                  // we need both the tree id and top box ref to change trees
                  value={`${currentTree?.treeID}-${currentTree?.TopBoxRef}`}
                  maxHeight={500}
                  options={treeOptions}
                />
              </div>
            )}
            <Tree
              data={data}
              onClick={subchartId => handleSubChartClick(doc, scm, subchartId, setRouteParam)}
              active={scm.GetCurrentSubChartID()}
              getNodeTestId={value => {
                return `sc-expand-btn-${value}`;
              }}
            />
          </div>
        </Overlay>
      )}
    </Dragger>
  );
};

export default SubchartsCard;
